/**
 * Admin → Audit log.
 *
 * Filterable list of every authorized tool call. Args JSON is collapsed by
 * default, expandable per-row. Filters: user (dropdown), tool (dropdown),
 * since (date), limit (number).
 */
import { useMemo, useState } from "react";

import { adminApi } from "../../api/admin";
import { JsonExpand } from "../../components/admin/JsonExpand";
import { PageHeader } from "../../components/PageHeader";
import { QueryBoundary } from "../../components/QueryBoundary";
import { formatRelative } from "../../lib/formatRelative";

export function AuditPage() {
  const usersQuery = adminApi.users.list();
  const toolsQuery = adminApi.tools.list();

  const [user, setUser] = useState("");
  const [tool, setTool] = useState("");
  const [since, setSince] = useState("");
  const [limit, setLimit] = useState(100);

  const query = adminApi.audit.query({
    user: user || undefined,
    tool: tool || undefined,
    since: since || undefined,
    limit,
  });

  const userOptions = useMemo(
    () => (usersQuery.data ?? []).map((u) => u.email).sort(),
    [usersQuery.data],
  );
  const toolOptions = useMemo(
    () => (toolsQuery.data ?? []).map((t) => t.name).sort(),
    [toolsQuery.data],
  );

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every authorized tool invocation. Filter by user, tool, or recency."
      />

      <div className="flex items-end gap-4 mb-5 flex-wrap">
        <div>
          <label className="label" htmlFor="audit-user">
            User
          </label>
          <select
            id="audit-user"
            className="input"
            style={{ minWidth: 220 }}
            value={user}
            onChange={(e) => setUser(e.target.value)}
          >
            <option value="">All users</option>
            {userOptions.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="audit-tool">
            Tool
          </label>
          <select
            id="audit-tool"
            className="input"
            style={{ minWidth: 200 }}
            value={tool}
            onChange={(e) => setTool(e.target.value)}
          >
            <option value="">All tools</option>
            {toolOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="audit-since">
            Since
          </label>
          <input
            id="audit-since"
            className="input"
            type="date"
            style={{ minWidth: 160 }}
            value={since}
            onChange={(e) => setSince(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="audit-limit">
            Limit
          </label>
          <input
            id="audit-limit"
            className="input"
            type="number"
            min={1}
            max={1000}
            style={{ width: 100 }}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) || 100)}
          />
        </div>
        <div className="ml-auto text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">
            {query.data?.length ?? 0}
          </span>{" "}
          rows
        </div>
      </div>

      <QueryBoundary
        query={query}
        empty={
          <div className="empty-state">
            <div className="title">No audit rows</div>
            <p className="hint">No tool calls match your filters.</p>
          </div>
        }
        isEmpty={(c) => c.length === 0}
      >
        {(rows) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Tool</th>
                  <th>Result</th>
                  <th>Args</th>
                  <th>MCP session</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="text-sm text-ink-low tnum">
                      {formatRelative(row.created_at)}
                    </td>
                    <td className="text-sm text-ink-medium">{row.user_email}</td>
                    <td>
                      <span className="font-mono text-sm text-ink">
                        {row.tool_name}
                      </span>
                    </td>
                    <td>
                      <ResultPill code={row.result_code} />
                    </td>
                    <td>
                      <JsonExpand value={row.args_json} />
                    </td>
                    <td className="text-xs text-ink-low font-mono">
                      {row.mcp_session_id
                        ? `${row.mcp_session_id.slice(0, 10)}…`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

function ResultPill({ code }: { code: string }) {
  const lower = code.toLowerCase();
  let cls = "pill pill--muted";
  if (lower === "ok" || lower === "success" || lower.startsWith("2")) {
    cls = "pill pill--ok";
  } else if (lower.includes("denied") || lower.includes("error") || lower.startsWith("5") || lower.startsWith("4")) {
    cls = "pill pill--signal";
  }
  return <span className={cls}>{code}</span>;
}
